import { Descriptions, Flex, Text } from "@/atoms"
import { getExploitabilityByKeys } from "@/const/insights-constants"
import { isVulnerablePackageType } from "@/domain/issue"
import { SeverityTag } from "@/molecules/SeverityTag"
import { InsightsTriageCell } from "@/pages/TriagePage/components/molecules/InsightsTriageCell"

import type { ScoreExplanationProps } from "@/api/useTriage.types"
import type { TFunction } from "@/hooks/useTranslation"

export const getItems = (
  record: ScoreExplanationProps,
  { t, internetFacingFF }: { t: TFunction; internetFacingFF?: boolean }
) => {
  const insights = {
    tag: true,
    runtime: false,
    executed: "NO" as const,
    internetFacing: false,
    ingress: false,
    inTheWildExploitMaturity: false,
    pocExploitMaturity: false,
    isDevDependency: false
  }

  const { riskInsights, epss, severity, issueType, cvssScore } = record
  const exposure =
    riskInsights.internetFacing === "YES" || !!riskInsights.ingress
  const usage = riskInsights.isDevDependency === "YES" || !!riskInsights.runtime
  const emptyValue = "-"

  const items = [
    {
      key: "severity",
      label: <Descriptions.Label>{t("general.severity")}</Descriptions.Label>,
      children: (
        <Flex gap={8}>
          <SeverityTag t={t} severity={severity} />
          {!!cvssScore.score && (
            <Flex>
              <Flex align="center" gap={3}>
                CVSS
                {!!cvssScore.version && (
                  <Text type="secondary" className="sm-text-ks">
                    ({cvssScore.version})
                  </Text>
                )}
              </Flex>
              :{` ${cvssScore.score}`}
            </Flex>
          )}
        </Flex>
      )
    },
    {
      key: "usage",
      label: <Descriptions.Label>{t("general.usage")}</Descriptions.Label>,
      className: "tag-score-explanation",
      children: usage
        ? InsightsTriageCell(
            t,
            !!internetFacingFF
          )({
            ...insights,
            runtime: riskInsights.runtime,
            executed: riskInsights.executed,
            isDevDependency: riskInsights.isDevDependency === "YES"
          })
        : emptyValue
    },
    {
      key: "exposure",
      label: <Descriptions.Label>{t("general.exposure")}</Descriptions.Label>,
      children: exposure
        ? InsightsTriageCell(
            t,
            !!internetFacingFF
          )({
            ...insights,
            internetFacing: riskInsights.internetFacing === "YES",
            ingress: riskInsights.ingress
          })
        : emptyValue,
      className: "tag-score-explanation"
    }
  ]

  if (isVulnerablePackageType(issueType)) {
    const epssValue = epss ? `${(Number(epss) * 100).toFixed(2)}%` : epss

    items.splice(
      2,
      0,
      {
        key: "epss",
        label: <Descriptions.Label>EPSS</Descriptions.Label>,
        children: (epssValue ?? emptyValue) as any
      },
      {
        key: "exploitMaturity",
        label: <Descriptions.Label>Exploit Maturity</Descriptions.Label>,
        children:
          riskInsights.exploitMaturity !== "UNDETERMINED"
            ? InsightsTriageCell(
                t,
                !!internetFacingFF
              )({
                ...insights,
                ...getExploitabilityByKeys(riskInsights.exploitMaturity)
              })
            : emptyValue,
        className: "tag-score-explanation"
      }
    )
  }

  return items
}
